import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { auth } from "../../firebase.js";
import Select from "react-select";
import ReactQuill, { Quill } from "react-quill-new";
import { FONT_TOOLBAR_ENTRY } from "../../utils/quillFonts";
import QuillTableBetter from "quill-table-better";
import "react-quill-new/dist/quill.snow.css";
import "quill-table-better/dist/quill-table-better.css";
import * as pdfjsLib from "pdfjs-dist";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";
import WorkspaceSidebar from "./WorkspaceSidebar.js";
import "../../css/Create.css";
import "../../css/Account.css";
import "../../css/DocumentationMainPage.css";
import "../../css/CreateWalkthrough.css";
import "../../css/DocumentationHomeComponent.css"
import "../../css/QuillOverrides.css";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css"

// Task Reference
import "../shared/TaskReferenceBlot";
import TaskReferenceDropdown from "../shared/TaskReferenceDropdown";
import { useTaskReference } from "../../utils/useTaskReference";
import "../shared/UserMentionBlot";
import UserMentionDropdown from "../shared/UserMentionDropdown";
import "../../css/TaskReference.css";
import "../../css/DocEditorMakeover.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);
const Inline = Quill.import("blots/inline");
class InlineCode extends Inline {
  static create() {
    const node = super.create();
    node.setAttribute("class", "inline-code");
    return node;
  }
  static formats(node) {
    return true;
  }
}
InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";

function CreateWorkspaceWalkthrough() {
    const { customDomain } = useParams();
    const [workspaceTheme, setWorkspaceTheme] = useState(null);
    const [ownerIsSubscriber, setOwnerIsSubscriber] = useState(false);
    const [uid, setUid] = useState("");
    const [hasWorkspace, setHasWorkspace] = useState(true);
    const { enhancedRightClickEnabled } = useQuickLinks();
    const [workspaceDetails, setWorkspaceDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [documentOrder, setDocumentOrder] = useState([]);
    const quillRef = useRef(null);
    const { taskRefProps, userMentionProps } = useTaskReference(quillRef, { mode: "workspace", workspaceId: workspaceDetails?.id });
    const [walkthroughName, setWalkthroughName] = useState("");
    const [description, setDescription] = useState("");
    const [tutorialCover, setTutorialCover] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [lastUpdatedBy, setLastUpdatedBy] = useState("");
    const [error, setError] = useState(null);
    const [availableDocuments, setAvailableDocuments] = useState([]);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const navigate = useNavigate();

    const walkthroughNameMaxLength = 150;
    const walkthroughCoverMaxLength = 10000

    const [emails, setEmails] = useState([]);
    const [currentEmail, setCurrentEmail] = useState("");
    const PERMISSION_OPTIONS = [
        { value: "ADMIN", label: "Administrator" },
        { value: "EDITOR", label: "Editor" },
        { value: "VIEWER", label: "Viewer" },
    ];

    // Fetch workspace details by customDomain only after uid is set
    useEffect(() => {
        async function fetchWorkspaceByCustomDomain(domain) {
            try {
                setLoadingMessage('Validating workspace...');
                setLoading(true);
                const user = auth.currentUser;
                if (!user) {
                    setHasWorkspace(false);
                    setWorkspaceDetails(null);
                    setError("User not authenticated");
                    setLoading(false);
                    return;
                }
                setLoadingMessage('Fetching workspace details...');
                const token = await user.getIdToken();
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/domain/${domain}/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': user.uid
                    }
                });
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || "Workspace not found");
                }
                const data = await response.json();
                setWorkspaceDetails(data);
                setHasWorkspace(true);
                setError(null);
                setWorkspaceTheme(data.theme);
                setOwnerIsSubscriber(data.owner?.is_subscriber);
            } catch (err) {
                setHasWorkspace(false);
                setWorkspaceDetails(null);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
        if (customDomain && uid) {
            fetchWorkspaceByCustomDomain(customDomain);
        }
    }, [customDomain, uid]);

    // Theme application logic (from CreateWorkspaceDocument.js)



    // Get current user UID
    useEffect(() => {
        setLoadingMessage('Checking authentication...');
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) setUid(user.uid);
            else setUid("");
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        async function fetchWorkspaceDocuments() {
            try {
                setDocumentsLoading(true);
                setLoadingMessage('Fetching workspace documents...');
                const user = auth.currentUser;
                const token = user ? await user.getIdToken() : null;
                const userUid = user ? user.uid : null;
                if (!token || !workspaceDetails) return;
                const workspaceId = workspaceDetails.id;
                // Fetch spaces and pages concurrently
                setLoadingMessage('Loading spaces and pages...');
                const [spacesResponse, pagesResponse] = await Promise.all([
                    fetch(
                        `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/spaces/`,
                        {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                                "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                "Requesting-User-Uid": userUid,
                            },
                        }
                    ),
                    fetch(
                        `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pages/`,
                        {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                                "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                "Requesting-User-Uid": userUid,
                            },
                        }
                    ),
                ]);
                setLoadingMessage('Processing workspace content...');
                const spacesData = spacesResponse.status === 200 ? await spacesResponse.json() : [];
                const spaceOptions = Array.isArray(spacesData)
                    ? spacesData.map((space) => ({
                        label: space.name,
                        value: `space-${space.id}`,
                        id: space.id,
                        type: "space",
                    }))
                    : [];
                const pagesData = pagesResponse.status === 200 ? await pagesResponse.json() : [];
                const pageOptions = Array.isArray(pagesData)
                    ? pagesData.map((page) => ({
                        label: page.page_name,
                        value: `page-${page.id}`,
                        id: page.id,
                        type: "page",
                    }))
                    : [];
                setAvailableDocuments([...spaceOptions, ...pageOptions]);
            } catch (error) {
                setAvailableDocuments([]);
            } finally {
                setDocumentsLoading(false);
            }
        }
        if (workspaceDetails) fetchWorkspaceDocuments();
    }, [workspaceDetails]);

    // Add document to selectedDocuments (from Select or button)
    const handleAddDocument = (doc) => {
        if (!selectedDocuments.some((d) => d.id === doc.id && d.type === doc.type)) {
            setSelectedDocuments([...selectedDocuments, doc]);
            setDocumentOrder([...documentOrder, `${doc.type}-${doc.id}`]);
        }
    };

    // Remove document from selectedDocuments
    const handleRemoveDocument = (index) => {
        const updated = [...selectedDocuments];
        updated.splice(index, 1);
        setSelectedDocuments(updated);
        setDocumentOrder(updated.map((d) => `${d.type}-${d.id}`));
    };

    // Reorder documents in selectedDocuments
    const handleReorderDocuments = (startIndex, endIndex) => {
        const updated = [...selectedDocuments];
        const [removed] = updated.splice(startIndex, 1);
        updated.splice(endIndex, 0, removed);
        setSelectedDocuments(updated);
        setDocumentOrder(updated.map((d) => `${d.type}-${d.id}`));
    };

    // Quill modules config for walkthrough cover (copied from CreateWalkthrough.js)
    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    [FONT_TOOLBAR_ENTRY],
                    [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
                    [{ size: ["small", false, "large"] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ color: [] }, { background: [] }],
                    [{ script: "sub" }, { script: "super" }],
                    [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
                    [{ indent: "-1" }, { indent: "+1" }],
                    [{ align: [] }],
                    ["blockquote", "code-block"],
                    ["link", "image", "video"],
                    ["inline-code"],
                    ["formula"],
                    ["table-better"],
                    ["clean"],
                    ["image"],
                    ["undo", "redo"],
                ],
                handlers: {
                    // Optionally add undo/redo handlers if needed
                },
            },
            history: {
                delay: 1000,
                maxStack: 100,
                userOnly: true,
            },
            imageResize: {
                parchment: Quill.import("parchment"),
                modules: ["Resize", "DisplaySize"],
            },
            table: false,
            "table-better": {
                language: "en_US",
                menus: [
                    "column",
                    "row",
                    "merge",
                    "table",
                    "cell",
                    "wrap",
                    "copy",
                    "delete",
                ],
                toolbarTable: true,
                tableCreationAttributes: {
                    className: 'responsive-table',
                    style: 'width: 100%; max-width: 100%;'
                }
            },
            keyboard: {
                bindings: QuillTableBetter.keyboardBindings,
            },
        }),
        []
    );

    const handleAddEmail = () => {
        if (
            currentEmail &&
            !emails.some((member) => member.email === currentEmail)
        ) {
            setEmails([
                ...emails,
                {
                    email: currentEmail,
                    role: "EDITOR", // Default role
                },
            ]);
            setCurrentEmail("");
        } else if (emails.some((member) => member.email === currentEmail)) {
            alert("This email has already been added.");
        } else {
            alert("Please enter a valid email address.");
        }
    };
    const handleRemoveEmail = (index) => {
        const updatedEmails = [...emails];
        updatedEmails.splice(index, 1);
        setEmails(updatedEmails);
    };
    const handleRoleChange = (index, newRole) => {
        const updatedEmails = [...emails];
        updatedEmails[index].role = newRole;
        setEmails(updatedEmails);
    };

    // Handle walkthrough creation
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation checks
        if (!walkthroughName || walkthroughName.trim() === "") {
            alert("Please enter a name for your walkthrough.");
            return;
        }

        // Check that name only contains letters, numbers, spaces, periods, dashes, and underscores
        if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(walkthroughName)) {
            alert(
                "Walkthrough name must contain only letters, numbers, spaces, periods, dashes, and underscores."
            );
            return;
        }

        if (!workspaceDetails || !uid) return;
        setIsSubmitting(true);
        setLoadingMessage('Preparing walkthrough data...');
        setError(null);
        try {
            // Prepare walkthrough data
            const walkthroughData = {
                walkthroughName: walkthroughName,
                description,
                tutorialCover: tutorialCover,
                documentOrder: documentOrder,
                isPublic: isPublic,
                completed,
                authorUid: uid,
                lastUpdatedBy: uid,
            };
            // Add document-specific permissions if specified
            if (emails.length > 0) {
                walkthroughData.permissions = emails;
            }

            // Extract mention UIDs from Quill content and include for notifications
            const mentionedUids = (() => {
              const parser = new DOMParser();
              const parsed = parser.parseFromString(tutorialCover, 'text/html');
              const spans = parsed.querySelectorAll('[data-user-uid]');
              const uids = [...new Set([...spans].map(s => s.getAttribute('data-user-uid')).filter(Boolean))];
              return uids.filter(u => u !== uid);
            })();
            walkthroughData.mentionedUids = mentionedUids;

            setLoadingMessage('Submitting walkthrough...');
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                throw new Error("User is not authenticated.");
            }
            const res = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceDetails.id}/walkthroughs/create/`, {
                method: "POST",
                headers: { 
                    "Content-Type": "application/json",
                    "requesting-user-uid": uid,
                    "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                },
                body: JSON.stringify(walkthroughData),
            });
            
            if (res.ok) {
                const data = await res.json();
                const walkthroughId = data.walkthrough_id;
                if (!walkthroughId) throw new Error("Walkthrough ID not returned by API");
                navigate(`/workspace/${customDomain}/walkthroughs/${walkthroughId}`);
            } else {
                const errorData = await res.json();
                throw new Error(errorData.message || "Failed to create walkthrough.");
            }
        } catch (err) {
            console.error("Error creating walkthrough:", err);
            alert(err.message || "An error occurred while creating the walkthrough.");
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const loggedOutOptions = [
        {
            label: "Login",
            icon: <i className="fas fa-sign-in-alt"></i>,
            url: "/login",
            onClick: () => navigate("/login"),
        },
        {
            label: "Register",
            icon: <i className="fas fa-user-plus"></i>,
            url: "/register",
            onClick: () => navigate("/register"),
        },
        {
            label: "Try My Notebook",
            icon: <i className="fas fa-book"></i>,
            url: "/my-notebook",
            onClick: () => navigate("/my-notebook"),
        },
        {
            label: "Visit YouTube Channel",
            icon: <i className="fab fa-youtube"></i>,
            url: "https://www.youtube.com/@schoolofintology4062",
            onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
        },
        {
            label: "Join Discord",
            icon: <i className="fab fa-discord"></i>,
            url: "https://discord.gg/DYkESHwgVA",
            onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
        },
    ];

  const loggedInOptions = [
      {
        label: "Submit Walkthrough",
        icon: <i className="fas fa-paper-plane"></i>,
        onClick: handleSubmit,
      },
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => window.open("/", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Documents",
        url: "/documentation",
        onClick: () => window.open("/documentation", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Walkthroughs",
        url: "/walkthroughs",
        onClick: () => window.open("/walkthroughs", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Notebook",
        url: "/my-notebook",
        onClick: () => window.open("/my-notebook", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Workspace",
        url: "/workspaces",
        onClick: () => window.open("/workspaces", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Account Settings",
        url: `/account/${uid}`,
        onClick: () => window.open(`/account/${uid}`, "_blank", "noopener,noreferrer"),
      },
    ];

    const menuOptions = uid ? loggedInOptions : loggedOutOptions;
    const { showContextMenu, ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

    const handleCompletedToggle = (e) => { setCompleted(e.target.checked) }


    if (loading || isSubmitting || documentsLoading) {
        return (
            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                <div className="DocumentationHomeComponent__loading-animation-coffee-cup">
                    <div className="coffee-cup-loader">
                        <div className="cup">
                            <div className="cup-body"></div>
                            <div className="cup-handle"></div>
                            <div className="cup-base"></div>
                            <div className="cup-shadow"></div>
                            <div className="steam steam1"></div>
                            <div className="steam steam2"></div>
                            <div className="steam steam3"></div>
                        </div>
                    </div>
                </div>
                <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
            </div>
        );
    }

    if (!uid) {
        return (
            <div 
                className="DocumentationMainPage__div DocEditor DocEditor--ws"
                onContextMenu={handleContextMenu}
            >
                <div className="DocumentationMainPage__main-content">
                    <p className="large">It looks like you're not signed in.</p>
                    <p className="medium">
                        <strong>To create a walkthrough, you must have an account with us</strong>.
                        If you already have an account, you can login by clicking the Login button below. If you don't already have an account, you can create a new one by clicking the Register button below.
                    </p>
                    <button
                        onClick={() => navigate("/")}
                        style={{ marginRight: "10px" }}
                        className="ConflictResolver__button"
                    >
                        Home
                    </button>
                    <button
                        onClick={() => navigate("/login")}
                        style={{ marginRight: "10px" }}
                        className="ConflictResolver__button"
                    >
                        Login
                    </button>
                    <button
                        onClick={() => navigate("/register")}
                        className="ConflictResolver__button"
                    >
                        Register
                    </button>
                </div>
                {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }
    if (!hasWorkspace) return <div>Workspace not found.</div>;

    // Add this check for no documents - show dedicated message instead of form
    if (availableDocuments.length === 0) {
        return (
            <div 
                className="TeamWorkspaceMainPage__div DocEditor DocEditor--ws"
                onContextMenu={handleContextMenu}
            >
                <WorkspaceSidebar workspaceId={workspaceDetails?.id} customDomain={customDomain} workspaceTheme={workspaceTheme} isSharedWorkspace={true} />
                <div className="TeamWorkspaceMainPage__main-content">
                        <i className="fas fa-file-alt" style={{ 
                            fontSize: "64px", 
                            color: "var(--bb-text)", 
                            marginBottom: "20px",
                            opacity: 0.7
                        }}></i>
                        <h2 style={{ color: "var(--bb-text)", marginBottom: "20px" }}>
                            Create Documents First
                        </h2>
                        <p className="large" style={{ marginBottom: "30px" }}>
                            Before you can create a walkthrough, you need to have some documents (spaces or pages) in this workspace to include in it.
                        </p>
                        <p className="medium" style={{ marginBottom: "40px" }}>
                            Walkthroughs are guided tours through your existing workspace documentation. Once you have created some spaces or pages in this workspace, you can return here to build walkthroughs that guide users through them step by step.
                        </p>
                            <h3 style={{ color: "var(--bb-text)", marginBottom: "15px" }}>
                                What you can do next:
                            </h3>
                                <p style={{ color: "var(--bb-text)", marginBottom: "10px" }}>
                                    <strong>1. Create a Space</strong> - A container for organizing related pages in this workspace
                                </p>
                                <p style={{ color: "var(--bb-text)", marginBottom: "10px" }}>
                                    <strong>2. Create Pages</strong> - Individual documents with your workspace content
                                </p>
                                <p style={{ color: "var(--bb-text)" }}>
                                    <strong>3. Come back here</strong> - Build walkthroughs once you have content
                                </p>
                            <div className="CreateWalkthrough__button-container">
                            <button
                                onClick={() => navigate(`/workspace/${customDomain}/create/document`)}
                                className="ConflictResolver__button"
                            >
                                <i className="fas fa-plus"></i>
                                Create Your First Document
                            </button>
                            <button
                                onClick={() => navigate(`/workspace/${customDomain}`)}
                                className="ConflictResolver__button"
                            >
                                <i className="fas fa-folder-open"></i>
                                View Workspace
                            </button>
                            <button
                                onClick={() => navigate("/")}
                                className="ConflictResolver__button"
                            >
                                <i className="fas fa-home"></i>
                                Home
                            </button>
                        </div>
                        </div>
                        {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }

    return (
        <div 
            className="TeamWorkspaceMainPage__div DocEditor DocEditor--ws"
            onContextMenu={handleContextMenu}
        >
            <WorkspaceSidebar workspaceId={workspaceDetails?.id} customDomain={customDomain} workspaceTheme={workspaceTheme} isSharedWorkspace={true} />
            <div className="TeamWorkspaceMainPage__main-content">
                <h2>Create a Walkthrough</h2>
                <div className="Create__form-group">
                    <label>Name: </label>
                    <input
                        type="text"
                        className="Create__input-short-field"
                        value={walkthroughName}
                        onChange={(e) => setWalkthroughName(e.target.value)}
                        maxLength={walkthroughNameMaxLength}
                        placeholder={`Max ${walkthroughNameMaxLength} characters`}
                        required
                    />
                </div>
                <div className="Create__form-group">
                    <label>Description: </label>
                    <input
                        type="text"
                        className="Create__input-full-field"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>
                <div className="Create__form-group">
                    <label>Publish this walkthrough upon submitting?</label>
                    <label className="Create__switch">
                        <input
                            type="checkbox"
                            checked={completed}
                            onChange={handleCompletedToggle}
                        />
                        <span className="Create__slider round"></span>
                    </label>
                </div>
                {/* Document-specific permissions UI */}
                <div className="Create__form-group" style={{ flexDirection: "column", marginBottom: "60px" }}>
                    <label>Document-specific permissions</label>
                    <div className="Create__form-group-helper-text">
                        <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                        <p className="Create__helper-text">
                            These permissions are specific to this walkthrough, overriding workspace-level permissions. To learn more about group privileges, please visit <a 
                                href="https://bluebird-documentation.com/documentation/page/Workspace%20Permissions/NGzFPNeV9GY1g6q2PFX7SfQmRqw1" 
                                target="_blank" 
                                rel="noopener noreferrer">this link</a>.
                            <strong>
                                <i>
                                    &nbsp;To keep existing workspace permissions in place for all team members, leave this field blank.
                                </i>
                            </strong>
                        </p>
                    </div>
                    {emails.map((member, index) => (
                        <div
                            key={index}
                            className="Create__email-item"
                            style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}
                        >
                            <span>{member.email}</span>
                            <select
                                value={member.role}
                                className="Create__select"
                                onChange={(e) => handleRoleChange(index, e.target.value)}
                                style={{ marginLeft: "10px", padding: "5px" }}
                            >
                                {PERMISSION_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={() => handleRemoveEmail(index)}
                                style={{ marginLeft: "10px", background: "none", border: "none", cursor: "pointer" }}
                            >
                                <i className="fas fa-trash-alt" title="Remove Team Member" style={{ color: "white" }}></i>
                            </button>
                        </div>
                    ))}
                    <div className="Create__email-input-container">
                        <input
                            type="email"
                            className="Create__email-input"
                            placeholder="Enter Email Address"
                            value={currentEmail}
                            onChange={(e) => setCurrentEmail(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
                        />
                        <button onClick={handleAddEmail} className="Create__add-email-btn">
                            <i className="fas fa-plus"></i>
                        </button>
                    </div>
                </div>
                {/* Document selection and ordering UI (styled like CreateWalkthrough.js) */}
                <div className="Create__form-group CreateWalkthrough__document-order-div" style={{ flexDirection: "column", marginBottom: "30px" }}>
                    <label>Select and Order Documents (Spaces/Pages):</label>
                    <div className="Create__form-group-helper-text">
                        <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                        <p className="Create__helper-text">
                            To make your walkthrough, select which documents you'd like to include in the walkthrough (by searching for them or clicking on them). Then you can also adjust the order in which documents appear in the walkthrough as well below.
                        </p>
                    </div>
                    {/* Searchable select for documents */}
                    <Select
                        options={availableDocuments.map((doc) => ({
                            value: doc,
                            label: `${doc.type}: ${doc.label}`,
                        }))}
                        onChange={(selected) => {
                            if (selected && selected.value) handleAddDocument(selected.value);
                        }}
                        placeholder="Search for documents..."
                        isClearable
                        className="CreateWalkthrough__document-search"
                    />
                    {/* Available documents as buttons */}
                    <div className="CreateWalkthrough__available-documents">
                        {availableDocuments.filter(doc => !selectedDocuments.some(sel => sel.id === doc.id && sel.type === doc.type)).map((doc) => (
                            <button
                                key={doc.id + doc.type}
                                onClick={() => handleAddDocument(doc)}
                                className="CreateWalkthrough__add-document-button"
                            >
                                <strong>{doc.type}:</strong> {doc.label}
                            </button>
                        ))}
                    </div>
                    {/* Selected documents with order controls */}
                    <div className="CreateWalkthrough__selected-documents">
                        {selectedDocuments.map((doc, index) => (
                            <div
                                key={doc.id + doc.type}
                                className="CreateWalkthrough__selected-document-item"
                            >
                                <span>
                                    <strong>{doc.type}:</strong> {doc.label}
                                </span>
                                <button
                                    onClick={() => handleRemoveDocument(index)}
                                    className="CreateWalkthrough__remove-document-button"
                                >
                                    Remove
                                </button>
                                {index > 0 && (
                                    <button
                                        onClick={() => handleReorderDocuments(index, index - 1)}
                                        className="CreateWalkthrough__move-up-button"
                                    >
                                        Move Up
                                    </button>
                                )}
                                {index < selectedDocuments.length - 1 && (
                                    <button
                                        onClick={() => handleReorderDocuments(index, index + 1)}
                                        className="CreateWalkthrough__move-down-button"
                                    >
                                        Move Down
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                {/* Walkthrough Cover Content (Quill) */}
                <div className="Create__editor-div">
                    <div className="QuillOverrides__content-formatting">
                    <ReactQuill
                        ref={quillRef}
                        value={tutorialCover}
                        onChange={setTutorialCover}
                        modules={modules}
                        style={{ height: "70vh" }}
                    />
                    <TaskReferenceDropdown {...taskRefProps} />
                    <UserMentionDropdown {...userMentionProps} />
                    </div>
                </div>
                <div className="Create__form-group-helper-text CreateWalkthrough__tutorial-cover-helper-text">
                    <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                    <p className="Create__helper-text">
                        Characters: {tutorialCover.replace(/<[^>]*>/g, "").length} / {walkthroughCoverMaxLength}
                    </p>
                </div>

                {/* Submit Button */}
                <button
                    onClick={handleSubmit}
                    className="CreateWalkthrough__submit-button"
                    style={{ marginTop: "30px" }}
                >
                    Submit
                </button>
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default CreateWorkspaceWalkthrough